import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Building2, 
  Hash, 
  CreditCard, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Star, 
  CheckCircle, 
  XCircle,
  FileText,
  Calendar,
  Copy,
  ExternalLink
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const BankDetailsPage = () => {
  const navigate = useNavigate();
  const { bankId } = useParams();
  const { company } = useAuth();
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (company?.id && bankId) {
      loadBank();
    }
  }, [company, bankId]);

  const loadBank = async () => {
    try {
      setLoading(true);
      
      const bank = await dataHelpers.getBankById(company.id, bankId);
      if (bank) {
        setBank(bank);
      } else {
        toast.error('Banco não encontrado');
        navigate('/dashboard/banks');
      }
    } catch (error) {
      console.error('Error loading bank:', error);
      toast.error('Erro ao carregar dados do banco');
      navigate('/dashboard/banks');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Tem certeza que deseja excluir o banco "${bank.name}"?`)) {
      return;
    }

    try {
      await dataHelpers.deleteBank(bank.id);
      toast.success('Banco excluído com sucesso');
      navigate('/dashboard/banks');
    } catch (error) {
      console.error('Error deleting bank:', error);
      toast.error('Erro ao excluir banco');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copiado para a área de transferência`);
    }).catch(() => {
      toast.error('Erro ao copiar para a área de transferência');
    });
  };

  const formatPixKey = (key, type) => {
    if (!key) return '';
    
    switch (type) {
      case 'phone':
        return key.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
      case 'cpf':
        return key.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      case 'cnpj':
        return key.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      default:
        return key;
    }
  };

  const getPixTypeLabel = (type) => {
    const types = {
      email: 'E-mail',
      phone: 'Telefone',
      cpf: 'CPF',
      cnpj: 'CNPJ',
      random: 'Chave Aleatória'
    };
    return types[type] || type;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="large" />
        </div>
      
    );
  }

  if (!bank) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Banco não encontrado</p>
            <Link to="/dashboard/banks" className="mt-4 text-blue-600 hover:text-blue-700">
              Voltar para lista de bancos
            </Link>
          </div>
        </div>
      
    );
  }

  return (
    
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard/banks')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </button>
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-full">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h1 className="text-2xl font-bold text-gray-900">{bank.name}</h1>
                  {bank.is_main && (
                    <Star className="w-6 h-6 text-yellow-500 fill-current" />
                  )}
                </div>
                <p className="text-gray-600">Código {bank.bank_code}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Link to={`/dashboard/banks/${bank.id}/edit`}>
              <Button variant="outline">
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
            </Link>
            <Button variant="outline" onClick={handleDelete} className="text-red-600 hover:text-red-700">
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6">
            <div className="flex items-center">
              <div className={`p-3 rounded-full ${bank.active ? 'bg-green-100' : 'bg-red-100'}`}>
                {bank.active ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-600" />
                )}
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Status</p>
                <p className={`text-lg font-semibold ${bank.active ? 'text-green-600' : 'text-red-600'}`}>
                  {bank.active ? 'Ativo' : 'Inativo'}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className={`p-3 rounded-full ${bank.is_main ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                <Star className={`w-6 h-6 ${bank.is_main ? 'text-yellow-600 fill-current' : 'text-gray-400'}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Tipo</p>
                <p className="text-lg font-semibold text-gray-900">
                  {bank.is_main ? 'Principal' : 'Secundário'}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <CreditCard className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">PIX</p>
                <p className="text-lg font-semibold text-gray-900">
                  {bank.pix_key ? 'Configurado' : 'Não configurado'}
                </p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Dados Bancários */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Hash className="w-5 h-5 mr-2" />
              Dados Bancários
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-600">Código do Banco</p>
                  <p className="text-lg font-semibold text-gray-900">{bank.bank_code}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(bank.bank_code, 'Código')}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>

              {bank.agency && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Agência</p>
                    <p className="text-lg font-semibold text-gray-900">{bank.agency}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(bank.agency, 'Agência')}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              )}

              {bank.account_number && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Conta</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {bank.account_number}{bank.account_digit && `-${bank.account_digit}`}
                    </p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(`${bank.account_number}${bank.account_digit ? `-${bank.account_digit}` : ''}`, 'Conta')}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              )}

              {bank.pix_key && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-blue-900">Chave PIX</h4>
                    <button
                      onClick={() => copyToClipboard(bank.pix_key, 'Chave PIX')}
                      className="p-2 text-blue-400 hover:text-blue-600"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-sm text-blue-600 mb-1">{getPixTypeLabel(bank.pix_type)}</p>
                  <p className="font-mono text-sm text-blue-900 bg-white px-2 py-1 rounded">
                    {formatPixKey(bank.pix_key, bank.pix_type)}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Dados de Contato */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Phone className="w-5 h-5 mr-2" />
              Informações de Contato
            </h3>
            <div className="space-y-4">
              {bank.address && (
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Endereço</p>
                    <p className="text-gray-900">{bank.address}</p>
                  </div>
                </div>
              )}

              {bank.phone && (
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Telefone</p>
                    <p className="text-gray-900">{bank.phone}</p>
                  </div>
                </div>
              )}

              {bank.email && (
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">E-mail</p>
                    <p className="text-gray-900">{bank.email}</p>
                  </div>
                </div>
              )}

              {bank.website && (
                <div className="flex items-center space-x-3">
                  <Globe className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Website</p>
                    <a
                      href={bank.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 flex items-center"
                    >
                      {bank.website}
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Observações */}
          {bank.notes && (
            <Card className="p-6 lg:col-span-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Observações
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 whitespace-pre-wrap">{bank.notes}</p>
              </div>
            </Card>
          )}

          {/* Informações do Sistema */}
          <Card className="p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Informações do Sistema
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Data de Cadastro</p>
                  <p className="text-gray-900">{formatDate(bank.created_at)}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Última Atualização</p>
                  <p className="text-gray-900">{formatDate(bank.updated_at)}</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    
  );
};

export default BankDetailsPage;
